import {
  Button as MantineButton,
  Menu,
  type ButtonProps as MantineButtonProps,
} from "@mantine/core";
import type { ReactElement, ReactNode } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import clsx from "clsx";
import styles from "../Button.module.scss";
import type { ButtonVariant } from "../Button";

// `type` is optional so the pre-existing flat `{ label, value }` items keep working.
export type MenuButtonEntry = {
  type?: "item";
  label: string;
  value: string;
  color?: string;
  icon?: ReactElement;
};

export type MenuButtonDivider = { type: "divider" };

export type MenuButtonGroupLabel = { type: "label"; label: string };

export type MenuButtonItem = MenuButtonEntry | MenuButtonDivider | MenuButtonGroupLabel;

export type MenuButtonProps = {
  children?: ReactNode;
  isDisabled?: boolean;
  iconLeft?: ReactElement;
  variant?: ButtonVariant;
  size?: "small" | "base" | "large";
  items: MenuButtonItem[];
  onSelect?: (value: string) => void;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  width?: string;
  position?: "bottom-start" | "bottom-end" | "top-start" | "top-end";
} & Omit<MantineButtonProps, "variant" | "size">;

export function MenuButton(props: MenuButtonProps) {
  const {
    children,
    variant,
    size = "base",
    isDisabled,
    iconLeft,
    items,
    onSelect,
    width = "target",
    position,
    color: _color = "var(--text-primary-default)",
    ...rest
  } = props;

  const variantClassName = styles[`button-${variant}`] || "";
  const sizeClassName = styles[`button-size-${size}`];
  const finalClassName = isDisabled
    ? clsx(styles["button-disabled"], styles["base-button"], sizeClassName)
    : clsx(variantClassName, styles["base-button"], sizeClassName);

  const isOutlineVariant =
    variant === "secondary" ||
    variant === "tertiary" ||
    variant === "neutral-secondary" ||
    variant === "destructive-secondary";

  return (
    <Menu withinPortal width={width} position={position}>
      <Menu.Target>
        <MantineButton
          className={finalClassName}
          variant={isOutlineVariant ? "outline" : "filled"}
          disabled={isDisabled}
          {...rest}
          type="button"
        >
          {iconLeft && <span className={styles["icon-left"]}>{iconLeft}</span>}
          {children}
          <span className={styles["icon-right"]}>
            <IconChevronDown />
          </span>
        </MantineButton>
      </Menu.Target>

      <Menu.Dropdown style={{ minWidth: "fit-content" }}>
        {(items || []).map((item, index) => {
          if (item.type === "divider") {
            // Index-keyed: a menu may carry more than one divider.
            return <Menu.Divider key={`divider-${index}`} />;
          }

          if (item.type === "label") {
            return <Menu.Label key={`label-${index}-${item.label}`}>{item.label}</Menu.Label>;
          }

          return (
            <Menu.Item
              key={item.value}
              onClick={() => onSelect?.(item.value)}
              leftSection={item.icon}
              bd="none"
              color={item.color}
              style={{ textAlign: "left", whiteSpace: "nowrap" }}
            >
              {item.label}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}
